/* eslint-disable no-plusplus,no-underscore-dangle,no-nested-ternary */
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';
import { naturalSorting } from '@/utils/experience-utils';
import { VSpace } from '@/components/spacing';
import { Tooltip } from '@/components/tooltip';
import { getChanceToWin, getUplift } from '@/features/details/summary/util';
import {
  Header,
  SummaryHeader,
  useMetricName,
} from '@/features/details/summary/summary-header';
import {
  SummaryBody,
  SummaryValue,
  VersionName,
} from '@/features/details/summary/summary-value';
import { Card } from '@/components/card';
import { SummaryContext } from '@/features/details/summary/context';
import { SummaryResponse } from '@/webapi/use-analytics-api';
import {
  Experience,
  ExperienceStatus,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import { allMetricKinds, MetricKind } from '@/webapi/models';
import { maybe } from '@/features/details/utils';
import { CONTROL } from '@/features/details/shared';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';
import { DatePickerContext } from '@/features/details/date-pick/context';
import {
  allowedCustomMetrics,
  allowProfit,
  allowReturns,
} from '@/features/details/charts/more-metrics';
import { FeatureBit } from '@/webapi/use-auth-api';
import { useFeatureBit } from '@/features/account-context';
import {
  CIHedaer,
  ConfidenceIntervals,
} from '@/features/details/summary/confidence-interval';
import { MoreMetricsList } from '@/features/details/summary/see-more-reports-list';

function OtwTooltip({ text }: { text: string }) {
  return (
    <Tooltip
      zIndex={0}
      title="Odds to Win"
      text={
        <span>
          The probability Version A is Better than Version B. <br />
          Based on the primary goal
          <strong>( {text} )</strong>.
        </span>
      }
    />
  );
}

export function SummaryTable({
  status,
  experience,
  setExperience,
}: {
  status: ExperienceStatus;
  experience: Experience;
  setExperience: (e: Experience) => void;
}) {
  const {
    moreMetrics,
    metricsPerRow,
    showSummary,
    variants,
    hasOneVariant,
    data,
    primaryMetrics,
    largeHeaders,
    hasMoreMetrics,
    primaryGoal,
    hasMetric,
  } = useContext(SummaryContext);
  const isCustomMetricsEnabled = useFeatureBit(FeatureBit.CUSTOM_METRICS);
  const allMetrics = allMetricKinds(experience)
    .filter(allowProfit(experience))
    .filter(allowedCustomMetrics(isCustomMetricsEnabled, hasMetric))
    .filter(allowReturns(experience));
  const addMoreMetrics = useMemo(
    () =>
      allMetrics?.filter(
        (value) =>
          !primaryMetrics?.includes(value) && !moreMetrics?.includes(value),
      ),
    [experience, primaryMetrics, moreMetrics],
  );

  const { version } = useContext(DatePickerContext);
  let _variants = variants;
  if (version?.isCurrent && status === ExperienceStatus.PUBLISHED) {
    _variants = variants.filter((v) => v.publishedChance > 0);
  }
  const [shouldShowCI] = useQueryParam<boolean>(
    `showCI`,
    withDefault(BooleanParam, false),
  );
  const ciEnabled =
    useFeatureBit(FeatureBit.CONFIDENCE_INTERVALS) && shouldShowCI;

  const variant100 = _variants.find((v) => v.publishedChance === 100);
  if (variant100 && version?.isCurrent) {
    _variants = [variant100];
  }
  const { title: goalTitle } = useMetricName(primaryGoal, false, ``, ``);
  const confidenceIntervalOffset = ciEnabled && !hasOneVariant ? 1 : 0;

  if (maybe(() => _variants.length, 0) === 0 || !showSummary) {
    return null;
  }
  return (
    <>
      <DetailsCard hasOneVariant={hasOneVariant}>
        <SummaryRow
          key="iddqd"
          columns={metricsPerRow + confidenceIntervalOffset}
        >
          {!hasOneVariant && (
            <SummaryHeader isLarge={largeHeaders}>Variation</SummaryHeader>
          )}
          {primaryMetrics.map((h) => (
            <Header value={h} key={h} isLarge={largeHeaders} />
          ))}
          {!hasOneVariant && (
            <>
              <SummaryHeader>
                {ciEnabled && !hasOneVariant ? `OTW` : `Odds to Win`}
                <OtwTooltip text={goalTitle} />
              </SummaryHeader>
              {ciEnabled && <CIHedaer />}
            </>
          )}
        </SummaryRow>
        {_variants?.sort(naturalSorting).map((v) => (
          <SummaryRow
            key={v.id}
            columns={metricsPerRow + confidenceIntervalOffset}
          >
            {!hasOneVariant && <VersionName v={v} />}
            {primaryMetrics.map((metric) => (
              <SummaryValue key={metric} metric={metric} variant={v} />
            ))}
            {!hasOneVariant && (
              <SummaryBody>
                <span>
                  {getFormattedChanceToWin({
                    data,
                    v,
                    primaryGoal,
                    variants: _variants,
                  })}
                </span>
              </SummaryBody>
            )}
            {ciEnabled && !hasOneVariant && (
              <ConfidenceIntervals
                variant={v}
                uplift={getUplift(data, primaryGoal, variants, v.id)}
              />
            )}
          </SummaryRow>
        ))}
      </DetailsCard>
      {hasMoreMetrics && (
        <>
          <VSpace value={2} />
          <DetailsCard>
            <SummaryRow key="iddqd" columns={metricsPerRow}>
              {!hasOneVariant && <SummaryHeader>Variation</SummaryHeader>}
              {moreMetrics
                .filter((_, idx) => idx < 7)
                .map((metric) => (
                  <Header key={metric} value={metric} isLarge={largeHeaders} />
                ))}
            </SummaryRow>
            {_variants?.sort(naturalSorting).map((v) => (
              <SummaryRow key={v.id} columns={metricsPerRow}>
                {!hasOneVariant && <VersionName v={v} />}
                {moreMetrics
                  .filter((_, idx) => idx < 7)
                  .map((metric) => (
                    <SummaryValue key={metric} metric={metric} variant={v} />
                  ))}
              </SummaryRow>
            ))}
            <MoreMetricsList
              experience={experience}
              setExperience={setExperience}
              hasOneVariant={hasOneVariant}
              moreMetrics={moreMetrics}
              addMoreMetrics={addMoreMetrics}
            />
          </DetailsCard>
        </>
      )}
    </>
  );
}

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p: any) => p.columns}, 1fr);
  grid-column-gap: 2rem;
  text-align: start;
`;

const DetailsCard = styled(Card)`
  && {
    cursor: default;
    display: grid;
    position: relative;
    grid-row-gap: 2.5rem;
    padding: ${(p: { hasOneVariant: boolean }) =>
      p?.hasOneVariant ? `3.5rem 5rem 4rem 5rem` : `3.5rem`};
    border-radius: 2rem;
  }
`;

function isMultiVariantNoControl(
  hasControl: boolean,
  variants: Array<ExperienceVariant>,
) {
  return !hasControl && variants?.length >= 2;
}

export function getFormattedChanceToWin({
  data,
  v,
  primaryGoal,
  variants,
}: {
  data: SummaryResponse;
  v: ExperienceVariant;
  primaryGoal: MetricKind;
  variants: Array<ExperienceVariant>;
}) {
  let hasControl = dataHasControl(variants, data);
  if (isMultiVariantNoControl(hasControl, variants)) {
    data = clone(data);
    if (maybe(() => v.id === data.controlVariationId)) {
      v = clone(v);
      v.name = CONTROL;
    }
    hasControl = true;
  }
  let chanceToWin: number | string = getChanceToWin(data, v, primaryGoal);
  const isMultiVariantControl = variants?.length > 2 && v.name === CONTROL;

  if (!hasControl) {
    return `---`;
  }

  if (chanceToWin <= 1) {
    chanceToWin = `<1`;
  }
  return isMultiVariantControl ? `---` : `${chanceToWin}%`;
}
export function dataHasControl(
  variants: Array<ExperienceVariant>,
  data: SummaryResponse,
) {
  const dataVariantIds = data?.variants?.map((v) => v.variantId);
  return !!variants
    ?.filter((v) => dataVariantIds.includes(v.id))
    ?.map((v) => v.name)
    ?.find((n) => n === CONTROL);
}
